import {
  ClipboardCheck,
  FileText,
  HeartHandshake,
  ListPlus,
  type LucideIcon,
  RefreshCw,
  Search,
} from "lucide-react";

import type { CareProtocolOpportunity } from "@elation/api-client/src/stitch-web";

import { SectionHeader } from "@src/shared/components/general/SectionHeader";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { MarkdownRenderer } from "@src/shared/components/markdown/MarkdownRenderer";
import { Badge } from "@src/shared/components/ui/badge";
import { cn } from "@src/shared/lib/utils";
import { useSidebar } from "@src/shared/providers/sidebarProvider";

export const OpportunityColorSchema = {
  AMBER: "amber",
  BLUE: "blue",
  GREEN: "green",
  INDIGO: "indigo",
  PURPLE: "purple",
  TEAL: "teal",
} as const;

export type OpportunityColorSchemaValue =
  (typeof OpportunityColorSchema)[keyof typeof OpportunityColorSchema];

const SCHEMA_CLASS: Record<OpportunityColorSchemaValue, string> = {
  amber: "border-l-warning",
  blue: "border-l-accent",
  green: "border-l-success",
  indigo: "border-l-indigo-500",
  purple: "border-l-purple-500",
  teal: "border-l-teal-500",
};

const OPPORTUNITY_TYPE_COLOR_SCHEMA: Record<
  CareProtocolOpportunity["type"],
  OpportunityColorSchemaValue
> = {
  quality_measure_gap: OpportunityColorSchema.PURPLE,
  condition_recapture: OpportunityColorSchema.BLUE,
  condition_specificity: OpportunityColorSchema.INDIGO,
  suspect_condition: OpportunityColorSchema.AMBER,
  care_management: OpportunityColorSchema.TEAL,
};

const OPPORTUNITY_TYPE_ICON: Record<
  CareProtocolOpportunity["type"],
  LucideIcon
> = {
  quality_measure_gap: ClipboardCheck,
  condition_recapture: RefreshCw,
  condition_specificity: ListPlus,
  suspect_condition: Search,
  care_management: HeartHandshake,
};

const OPPORTUNITY_TYPE_LABEL: Record<CareProtocolOpportunity["type"], string> =
  {
    quality_measure_gap: "Quality Measure Gap",
    condition_recapture: "Condition Recapture",
    condition_specificity: "Condition Specificity",
    suspect_condition: "Suspect Condition",
    care_management: "Care Management",
  };

interface OpportunityProps {
  opportunity: CareProtocolOpportunity;
  isNew?: boolean;
}

/**
 * Card-style row for a single care protocol opportunity: description,
 * type badge, and sidebar detail view.
 */
export const Opportunity = ({
  opportunity,
  isNew = false,
}: OpportunityProps) => {
  const { openSidebar } = useSidebar();

  const { type } = opportunity;

  const colorSchema =
    OPPORTUNITY_TYPE_COLOR_SCHEMA[type] ?? OpportunityColorSchema.BLUE;
  const Icon = OPPORTUNITY_TYPE_ICON[type] ?? FileText;
  const typeLabel = OPPORTUNITY_TYPE_LABEL[type] ?? "Opportunity";

  return (
    <HorizontalFlexbox
      as="button"
      type="button"
      alignItems="flex-start"
      gap="sm"
      onClick={() => {
        openSidebar({
          name: "Opportunity",
          nameBadge: <Badge variant="subtle">{typeLabel}</Badge>,
          title: opportunity.description,
          subtitle: "",
          content: (
            <VerticalFlexbox gap="sm">
              {opportunity.clinicalReasoning ? (
                <>
                  <SectionHeader>Clinical Reasoning</SectionHeader>
                  <MarkdownRenderer markdown={opportunity.clinicalReasoning} />
                </>
              ) : null}
            </VerticalFlexbox>
          ),
        });
      }}
      className={cn(
        "group w-full rounded-lg border border-l-4 border-border bg-card px-3 py-2 text-left shadow-soft transition-colors",
        "hover:border-accent/40 hover:bg-accent/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        SCHEMA_CLASS[colorSchema],
        isNew && "ring-2 ring-accent/40",
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
      <VerticalFlexbox gap="xs" className="min-w-0 flex-1">
        <Text sizing="sm" fontWeight={500} className="line-clamp-2">
          {opportunity.description}
        </Text>
        {opportunity.clinicalReasoning ? (
          <Text
            sizing="sm"
            appearance="subdued"
            className="line-clamp-2 leading-snug"
          >
            {opportunity.clinicalReasoning}
          </Text>
        ) : null}
      </VerticalFlexbox>
      {isNew ? <Badge variant="accent">New</Badge> : null}
    </HorizontalFlexbox>
  );
};
